import type { DensityOption } from "@/features/theme/defaults";

export interface HeaderDensityConfig {
    /** CSS value for --header-height custom property (desktop) */
    headerHeight: string;
    /** CSS value for --header-height on mobile (used via JS, not media query) */
    mobileHeaderHeight: string;
    /** Responsive vertical padding on main header row */
    py: string;
    /** Responsive vertical padding on logo link */
    logoPy: string;
    /** Responsive Tailwind height class for logo image */
    logoHeight: string;
    /** Next.js Image width prop */
    logoImageWidth: number;
    /** Next.js Image height prop */
    logoImageHeight: number;
    /** Gap between nav items (desktop only) */
    navGap: string;
    /** Gap between logo and navigation (desktop only) */
    logoNavGap: string;
    /** Gap between right-side controls */
    controlsGap: string;
    /** Vertical padding on mobile inline items row */
    mobileItemsPy: string;
    /** Gap between mobile inline items */
    mobileItemsGap: string;
    /** Responsive font size class for nav items */
    textSize: string;
    /** Icon size in pixels for icon-only items */
    iconSize: number;
    /** Icon size in pixels for items with label */
    iconSizeWithLabel: number;
    /** Centered-logo variant: Image width prop */
    centeredLogoImageWidth: number;
    /** Centered-logo variant: Image height prop */
    centeredLogoImageHeight: number;
    /** Centered-logo variant: max-height class (responsive) */
    centeredLogoMaxHeight: string;
    /** Centered-logo variant: min-height class (responsive) */
    centeredLogoMinHeight: string;
    /** Centered-logo variant: nav bar vertical padding */
    navBarPy: string;
    /** Secondary menu bar vertical padding */
    secondaryPy: string;
    /** Secondary menu bar gap */
    secondaryGap: string;
    /** Brand row vertical padding (desktop only) */
    brandRowPy: string;
    /** Dropdown submenu container vertical padding */
    dropdownPy: string;
    /** Dropdown submenu item padding */
    dropdownItemPx: string;
    dropdownItemPy: string;
}

/**
 * Mobile caps: on small screens the larger density levels compress
 * so the header never exceeds ~70px. The desktop values scale freely.
 *
 * Mobile mapping:
 *   extra-compact → same
 *   compact       → same
 *   normal        → capped to compact-ish mobile values
 *   relaxed       → capped to ~60px mobile
 *   extra-relaxed → capped to ~70px mobile
 */
export const HEADER_DENSITY_CONFIG: Record<DensityOption, HeaderDensityConfig> = {
    "extra-compact": {
        headerHeight: "44px",
        mobileHeaderHeight: "44px",
        py: "py-1.5",
        logoPy: "py-0.5",
        logoHeight: "h-6",
        logoImageWidth: 90,
        logoImageHeight: 24,
        navGap: "gap-4",
        logoNavGap: "gap-6",
        controlsGap: "gap-3",
        mobileItemsPy: "py-1",
        mobileItemsGap: "gap-3",
        textSize: "text-xs",
        iconSize: 16,
        iconSizeWithLabel: 14,
        centeredLogoImageWidth: 120,
        centeredLogoImageHeight: 30,
        centeredLogoMaxHeight: "max-h-12",
        centeredLogoMinHeight: "min-h-7",
        navBarPy: "py-1",
        secondaryPy: "py-1.5",
        secondaryGap: "gap-4",
        brandRowPy: "py-1",
        dropdownPy: "py-1",
        dropdownItemPx: "px-3",
        dropdownItemPy: "py-1.5",
    },
    // "compact" matches the previous hardcoded header sizing
    compact: {
        headerHeight: "56px",
        mobileHeaderHeight: "56px",
        py: "py-3",
        logoPy: "py-1",
        logoHeight: "h-8",
        logoImageWidth: 120,
        logoImageHeight: 32,
        navGap: "gap-6",
        logoNavGap: "gap-8",
        controlsGap: "gap-4",
        mobileItemsPy: "py-2",
        mobileItemsGap: "gap-4",
        textSize: "text-sm",
        iconSize: 18,
        iconSizeWithLabel: 16,
        centeredLogoImageWidth: 160,
        centeredLogoImageHeight: 40,
        centeredLogoMaxHeight: "max-h-20",
        centeredLogoMinHeight: "min-h-10",
        navBarPy: "py-2",
        secondaryPy: "py-2.5",
        secondaryGap: "gap-6",
        brandRowPy: "py-1.5",
        dropdownPy: "py-2",
        dropdownItemPx: "px-4",
        dropdownItemPy: "py-2",
    },
    normal: {
        headerHeight: "68px",
        mobileHeaderHeight: "56px",
        py: "py-3 md:py-4",
        logoPy: "py-1",
        logoHeight: "h-8 md:h-9",
        logoImageWidth: 135,
        logoImageHeight: 36,
        navGap: "gap-7",
        logoNavGap: "gap-8 md:gap-9",
        controlsGap: "gap-4",
        mobileItemsPy: "py-2",
        mobileItemsGap: "gap-4",
        textSize: "text-sm",
        iconSize: 18,
        iconSizeWithLabel: 16,
        centeredLogoImageWidth: 180,
        centeredLogoImageHeight: 45,
        centeredLogoMaxHeight: "max-h-16 md:max-h-22",
        centeredLogoMinHeight: "min-h-10 md:min-h-11",
        navBarPy: "py-2.5",
        secondaryPy: "py-2.5",
        secondaryGap: "gap-6",
        brandRowPy: "py-2",
        dropdownPy: "py-2",
        dropdownItemPx: "px-4",
        dropdownItemPy: "py-2",
    },
    relaxed: {
        headerHeight: "82px",
        mobileHeaderHeight: "60px",
        py: "py-3 md:py-5",
        logoPy: "py-1",
        logoHeight: "h-8 md:h-10",
        logoImageWidth: 150,
        logoImageHeight: 40,
        navGap: "gap-8",
        logoNavGap: "gap-8 md:gap-10",
        controlsGap: "gap-4 md:gap-5",
        mobileItemsPy: "py-2",
        mobileItemsGap: "gap-4",
        textSize: "text-sm",
        iconSize: 20,
        iconSizeWithLabel: 18,
        centeredLogoImageWidth: 200,
        centeredLogoImageHeight: 50,
        centeredLogoMaxHeight: "max-h-16 md:max-h-24",
        centeredLogoMinHeight: "min-h-10 md:min-h-12",
        navBarPy: "py-3",
        secondaryPy: "py-2.5 md:py-3",
        secondaryGap: "gap-6 md:gap-7",
        brandRowPy: "py-2.5",
        dropdownPy: "py-2.5",
        dropdownItemPx: "px-5",
        dropdownItemPy: "py-2.5",
    },
    "extra-relaxed": {
        headerHeight: "100px",
        mobileHeaderHeight: "70px",
        py: "py-4 md:py-6",
        logoPy: "py-1 md:py-1.5",
        logoHeight: "h-9 md:h-12",
        logoImageWidth: 180,
        logoImageHeight: 48,
        navGap: "gap-8",
        logoNavGap: "gap-8 md:gap-10",
        controlsGap: "gap-4 md:gap-5",
        mobileItemsPy: "py-2",
        mobileItemsGap: "gap-4",
        textSize: "text-sm md:text-base",
        iconSize: 22,
        iconSizeWithLabel: 18,
        centeredLogoImageWidth: 240,
        centeredLogoImageHeight: 60,
        centeredLogoMaxHeight: "max-h-16 md:max-h-28",
        centeredLogoMinHeight: "min-h-10 md:min-h-14",
        navBarPy: "py-3",
        secondaryPy: "py-2.5 md:py-3.5",
        secondaryGap: "gap-6 md:gap-8",
        brandRowPy: "py-3",
        dropdownPy: "py-3",
        dropdownItemPx: "px-5",
        dropdownItemPy: "py-3",
    },
};

export function getHeaderDensity(density?: string): HeaderDensityConfig {
    return HEADER_DENSITY_CONFIG[(density as DensityOption) || "normal"] || HEADER_DENSITY_CONFIG.normal;
}
